export function createSessionRevalidationGuard() {
  let generation = 0
  return {
    begin(): number {
      generation += 1
      return generation
    },
    isCurrent(candidate: number): boolean {
      return candidate === generation
    },
  }
}

export async function clearAccountCacheIfCurrent({
  guard,
  generation,
  cancel,
  clear,
}: {
  guard: ReturnType<typeof createSessionRevalidationGuard>
  generation: number
  cancel: () => Promise<unknown>
  clear: () => void
}): Promise<boolean> {
  await cancel()
  if (!guard.isCurrent(generation))
    return false
  clear()
  return true
}

export interface AccountQueryScope<TClient> {
  queryClient: TClient
  generation: number
}

export function replaceAccountQueryScope<
  TClient extends { cancelQueries: () => Promise<unknown>, clear: () => void },
>(
  previous: AccountQueryScope<TClient>,
  nextQueryClient: TClient,
): AccountQueryScope<TClient> {
  void previous.queryClient.cancelQueries()
  previous.queryClient.clear()
  return {
    queryClient: nextQueryClient,
    generation: previous.generation + 1,
  }
}

export async function reconcileAccountCacheForSessionTruth<
  TUser extends { id: string },
>({
  guard,
  generation,
  previousUserId,
  observedUserId,
  truthUserId,
  cacheAlreadyCleared,
  cancel,
  clear,
  loadUser,
  writeUser,
  replaceUserCache,
}: {
  guard: ReturnType<typeof createSessionRevalidationGuard>
  generation: number
  previousUserId: string | undefined
  observedUserId: string | undefined
  truthUserId: string
  cacheAlreadyCleared: boolean
  cancel: () => Promise<unknown>
  clear: () => void
  loadUser: () => Promise<TUser>
  writeUser: (user: TUser) => void
  replaceUserCache: (user: TUser) => void
}): Promise<'unchanged' | 'applied' | 'replaced' | 'mismatch' | 'stale'> {
  const accountChanged
    = previousUserId !== undefined && previousUserId !== truthUserId
  const observedUserMismatch
    = observedUserId !== undefined && observedUserId !== truthUserId
  if (!cacheAlreadyCleared && !accountChanged && !observedUserMismatch)
    return 'unchanged'

  if (
    !cacheAlreadyCleared
    && accountChanged
    && !(await clearAccountCacheIfCurrent({
      guard,
      generation,
      cancel,
      clear,
    }))
  ) {
    return 'stale'
  }

  const refreshedUser = await loadUser()
  if (!guard.isCurrent(generation))
    return 'stale'
  if (refreshedUser.id !== truthUserId)
    return 'mismatch'
  if (accountChanged) {
    replaceUserCache(refreshedUser)
    return 'replaced'
  }
  writeUser(refreshedUser)
  return 'applied'
}
